/**
 * 出張旅費精算請求（戻入）書 - A4 landscape - matches PDF layout
 */
export default function TravelExpensePrintSheet({ submission }) {
  const data = submission.form_data || {};
  // 発行日 = the actual date the document is issued/printed (today), not the submission date.
  const today = new Date();

  const formatMd = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  // Map a leg's mode to which fare column it fills
  const COLUMN_FOR_MODE = {
    '鉄道': 'rail',
    '新幹線・特急': 'express',
    'バス': 'bus',
    'タクシー': 'road',
    '自家用車': 'road',
    '航空': 'rail',
    'その他': 'rail',
  };

  // Normalize legs from new schema OR fall back to legacy single-leg fields
  const legacyLegs = (!data.transport_legs || data.transport_legs.length === 0) && (data.departure || data.destination || data.transportation_fee)
    ? [{
        id: 'legacy',
        mode: '鉄道',
        from: data.departure || '',
        to: data.destination || '',
        fare: Number(data.transportation_fee) || 0,
        date: data.travel_date,
      }]
    : null;
  const legs = legacyLegs || (Array.isArray(data.transport_legs) ? data.transport_legs : []);

  const accommodation = Number(data.accommodation_fee) || 0;
  const allowance = Number(data.daily_allowance) || 0;
  const meal = Number(data.meal_fee) || 0;
  const transportation = legs.reduce((s, l) => s + (Number(l.fare) || 0), 0) || (Number(data.transportation_fee) || 0);
  const total = Number(data.total_amount) || (transportation + accommodation + allowance + meal + (Number(data.other_fees) || 0));

  // Column totals per fare type
  const colTotals = legs.reduce((acc, l) => {
    const col = COLUMN_FOR_MODE[l.mode] || 'rail';
    acc[col] = (acc[col] || 0) + (Number(l.fare) || 0);
    return acc;
  }, {});

  // Which 運賃 sub-columns are actually used by any leg — only those are shown.
  // Preserve canonical ordering: rail → express → bus → road.
  const ALL_FARE_COLS = [
    { key: 'rail', label: '鉄道' },
    { key: 'express', label: '特急料' },
    { key: 'bus', label: 'バス代' },
    { key: 'road', label: '道路代' },
  ];
  const activeFareCols = ALL_FARE_COLS.filter((c) => legs.some((l) => (COLUMN_FOR_MODE[l.mode] || 'rail') === c.key));
  // Always render at least one fare column so the table doesn't collapse on an empty leg list.
  const fareCols = activeFareCols.length > 0 ? activeFareCols : [ALL_FARE_COLS[0]];

  // Resolve 請求者 (first employee) and 同行者 (rest).
  // When multiple employees are selected, render one form PER employee where
  // each person is the primary 請求者 and the others appear as 同行者.
  const employeeNames = Array.isArray(data.employee_names) && data.employee_names.length
    ? data.employee_names
    : (typeof data.employee_name === 'string' && data.employee_name ? data.employee_name.split('、').filter(Boolean) : []);
  const formPerspectives = employeeNames.length > 0
    ? employeeNames.map((primary, i) => ({
        primary,
        accompanying: employeeNames.filter((_, j) => j !== i),
      }))
    : [{ primary: data.employee_name || '', accompanying: [] }];

  // One print row per leg — 発/着 stay together inside a single box.
  // Inline horizontal arrangement: "発・着" labels + "depart_time・arrive_time" times.
  const printRows = legs.map((leg, legIdx) => {
    const col = COLUMN_FOR_MODE[leg.mode] || 'rail';
    const fare = Number(leg.fare) || 0;
    const date = leg.date || leg.depart_date || leg.arrive_date || (legIdx === 0 ? (data.travel_start_date || data.travel_date) : '');
    const dep = leg.depart_time || (leg.kind === '発' ? leg.time : '') || '';
    const arr = leg.arrive_time || (leg.kind === '着' ? leg.time : '') || '';
    return { date, from: leg.from, to: leg.to, mode: leg.mode, dep, arr, fare, col };
  });
  const TOTAL_PRINT_ROWS = 8;

  const cell = { border: '1px solid #000', padding: '3px 4px', fontSize: '9px', verticalAlign: 'middle' };
  const header = { ...cell, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#fff' };

  return (
    <>
    {formPerspectives.map((perspective, perspIdx) => {
      const primaryEmployee = perspective.primary;
      const accompanying = perspective.accompanying;
      return (
    <div
      key={`form-${perspIdx}`}
      className="print-form bg-white preview-travel"
      style={{
        padding: '8px',
        fontFamily: "'IBM Plex Sans JP', 'Yu Gothic', sans-serif",
        color: '#000',
        background: '#fff',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        ...(perspIdx > 0 ? { pageBreakBefore: 'always', breakBefore: 'page' } : {}),
      }}
    >
      {/* Top section: title + account codes/approval boxes */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
        <tbody>
          <tr>
            <td style={{ verticalAlign: 'middle', width: '40%', paddingLeft: '4px' }}>
              <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 6px 0', lineHeight: '1.1', letterSpacing: '0.02em' }}>
                出張旅費精算請求（戻入）書
              </h1>
              {(() => {
                const issueY = today.getFullYear();
                const issueM = String(today.getMonth() + 1).padStart(2, '0');
                const issueD = String(today.getDate()).padStart(2, '0');
                const labelStyle = { display: 'inline-block', width: '72px', fontWeight: 'normal', color: '#333' };
                const yearSlot = { display: 'inline-block', width: '46px', textAlign: 'center', color: '#000' };
                const mdSlot = { display: 'inline-block', width: '30px', textAlign: 'center', color: '#000' };
                const unit = { display: 'inline-block', width: '20px', textAlign: 'center', color: '#555' };
                const rowStyle = {
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.4',
                  paddingBottom: '3px',
                  borderBottom: '1px solid #d4d4d4',
                  marginBottom: '4px',
                };
                return (
                  <div style={{ display: 'inline-block', paddingRight: '8px' }}>
                    <div style={rowStyle}>
                      <span style={labelStyle}>発行日</span>
                      <span style={yearSlot}>{issueY}</span>
                      <span style={unit}>年</span>
                      <span style={mdSlot}>{issueM}</span>
                      <span style={unit}>月</span>
                      <span style={mdSlot}>{issueD}</span>
                      <span style={unit}>日</span>
                    </div>
                    <div style={{ ...rowStyle, marginBottom: 0 }}>
                      <span style={labelStyle}>経理計上日</span>
                      <span style={yearSlot}></span>
                      <span style={unit}>年</span>
                      <span style={mdSlot}></span>
                      <span style={unit}>月</span>
                      <span style={mdSlot}></span>
                      <span style={unit}>日</span>
                    </div>
                  </div>
                );
              })()}
            </td>
            <td style={{ width: '45%', verticalAlign: 'top', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ height: '16px' }}>
                    <td rowSpan={4} className="vertical-text" style={{ ...header, width: '6%' }}>借方</td>
                    <td style={header} colSpan={2}>勘定科目名<br />勘定科目コード</td>
                    <td rowSpan={4} className="vertical-text" style={{ ...header, width: '6%' }}>貸方</td>
                    <td style={header} colSpan={2}>勘定科目名<br />勘定科目コード</td>
                  </tr>
                  <tr style={{ height: '14px' }}>
                    <td style={{ ...cell, width: '5%', textAlign: 'center' }}>①</td>
                    <td style={cell}></td>
                    <td style={{ ...cell, width: '5%', textAlign: 'center' }}>①</td>
                    <td style={cell}></td>
                  </tr>
                  <tr style={{ height: '14px' }}>
                    <td style={{ ...cell, textAlign: 'center' }}>②</td>
                    <td style={cell}></td>
                    <td style={{ ...cell, textAlign: 'center' }}>②</td>
                    <td style={cell}></td>
                  </tr>
                  <tr style={{ height: '14px' }}>
                    <td style={{ ...cell, textAlign: 'center' }}>③</td>
                    <td style={cell}></td>
                    <td style={{ ...cell, textAlign: 'center' }}>③</td>
                    <td style={cell}></td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td style={{ width: '15%', verticalAlign: 'bottom', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td rowSpan={1} className="vertical-text" style={{ ...header, width: '14%', height: '50px' }}>請求元</td>
                    <td style={{ ...cell, height: '50px', width: '28%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px', fontWeight: 'bold' }}>承認</td>
                    <td style={{ ...cell, height: '50px', width: '28%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px', fontWeight: 'bold' }}>照査</td>
                    <td style={{ ...cell, height: '50px', width: '28%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px' }}></td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Applicant row */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
        <tbody>
          {/* Row 1: top-level headers */}
          <tr style={{ height: '18px' }}>
            <td colSpan={2} style={{ ...header, width: '14%' }}>入出金区分</td>
            <td style={{ ...header, width: '10%' }}>所属コード<br /><span style={{ fontSize: '7px' }}>（経費負担元）</span></td>
            <td style={{ ...header, width: '14%' }}>所属部課名</td>
            <td colSpan={4} style={header}>請求者</td>
            <td rowSpan={3} className="vertical-text" style={{ ...header, width: '4%' }}>同行者</td>
            <td rowSpan={3} style={{ ...header, width: '14%', verticalAlign: 'top', textAlign: 'left', fontWeight: 'normal', fontSize: '9px', lineHeight: '1.4', padding: '4px' }}>
              {accompanying.map((n, i) => (<div key={`${n}-${i}`}>{n}</div>))}
            </td>
          </tr>
          {/* Row 2: 入出金区分 sub-values + applicant values + 請求者 sub-headers */}
          <tr style={{ height: '18px' }}>
            <td rowSpan={2} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: '11px', width: '7%' }}>通常</td>
            <td rowSpan={2} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: '11px', width: '7%' }}>入出金</td>
            <td rowSpan={2} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: '13px', fontFamily: 'monospace' }}></td>
            <td rowSpan={2} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>{data.department || ''}</td>
            <td style={{ ...header, width: '6%' }}>ID</td>
            <td style={{ ...header, width: '7%' }}>コード</td>
            <td style={header}>氏　名</td>
            <td rowSpan={2} style={{ ...cell, width: '5%', verticalAlign: 'top', textAlign: 'left', padding: '2px 4px' }}>
              <span style={{ fontSize: '9px', fontWeight: 'bold', lineHeight: '1' }}>印</span>
            </td>
          </tr>
          {/* Row 3: 請求者 values */}
          <tr style={{ height: '24px' }}>
            <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontFamily: 'monospace', fontSize: '11px' }}>{data.id_card || ''}</td>
            <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontFamily: 'monospace' }}>{data.employee_code || ''}</td>
            <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>{primaryEmployee}</td>
          </tr>
        </tbody>
      </table>

      {/* Main expense table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...header, width: '5%' }}>月日</th>
            <th rowSpan={2} style={{ ...header, width: '20%' }}>出発地・到着地</th>
            <th rowSpan={2} style={{ ...header, width: '4%' }}>発着</th>
            <th rowSpan={2} style={{ ...header, width: '6%' }}>時間</th>
            <th colSpan={4} style={header}>運　　賃</th>
            <th rowSpan={2} style={{ ...header, width: '7%' }}>宿泊料</th>
            <th rowSpan={2} style={{ ...header, width: '5%' }}>日当</th>
            <th rowSpan={2} style={{ ...header, width: '5%' }}>食事</th>
            <th rowSpan={2} style={{ ...header, width: '21%' }}>出張先・用件</th>
          </tr>
          <tr>
            <th style={{ ...header, width: '5%' }}>{legs.some((l) => (COLUMN_FOR_MODE[l.mode] || 'rail') === 'rail') ? '鉄道' : ''}</th>
            <th style={{ ...header, width: '5%' }}>{legs.some((l) => COLUMN_FOR_MODE[l.mode] === 'express') ? '特急料' : ''}</th>
            <th style={{ ...header, width: '5%' }}>{legs.some((l) => COLUMN_FOR_MODE[l.mode] === 'bus') ? 'バス代' : ''}</th>
            <th style={{ ...header, width: '5%' }}>{legs.some((l) => COLUMN_FOR_MODE[l.mode] === 'road') ? '道路代' : ''}</th>
          </tr>
        </thead>
        <tbody>
          {/* One print row per leg — 発/着 share a single box, times share a single box */}
          {printRows.map((row, idx) => (
            <tr key={`${row.date || ''}-${row.from || ''}-${idx}`} style={{ height: '21px' }}>
              <td style={{ ...cell, textAlign: 'center' }}>{formatMd(row.date)}</td>
              <td style={cell}>
                {row.from} ～ {row.to}
                {row.mode && <span style={{ marginLeft: '6px', color: '#444', fontSize: '8px' }}>（{row.mode}）</span>}
              </td>
              <td style={{ ...cell, textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {row.dep ? '発' : ''}{row.dep && row.arr ? '・' : ''}{row.arr ? '着' : ''}
              </td>
              <td style={{ ...cell, textAlign: 'center', fontSize: '9px', whiteSpace: 'nowrap' }}>
                {row.dep}{row.dep && row.arr ? '・' : ''}{row.arr}
              </td>
              <td style={{ ...cell, textAlign: 'right' }}>{row.col === 'rail' && row.fare > 0 ? row.fare.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{row.col === 'express' && row.fare > 0 ? row.fare.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{row.col === 'bus' && row.fare > 0 ? row.fare.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{row.col === 'road' && row.fare > 0 ? row.fare.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{idx === 0 && accommodation > 0 ? accommodation.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{idx === 0 && allowance > 0 ? allowance.toLocaleString() : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{idx === 0 && meal > 0 ? meal.toLocaleString() : ''}</td>
              <td style={cell}>{idx === 0 ? (data.purpose || '') : ''}</td>
            </tr>
          ))}
          {/* Padding rows up to 12 total */}
          {Array.from({ length: Math.max(0, TOTAL_PRINT_ROWS - printRows.length) }).map((_, i) => (
            <tr key={`pad-${i}`} style={{ height: '21px' }}>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
              <td style={cell}></td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{ ...header, textAlign: 'center' }}>計</td>
            <td style={cell}></td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{colTotals.rail ? colTotals.rail.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{colTotals.express ? colTotals.express.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{colTotals.bus ? colTotals.bus.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{colTotals.road ? colTotals.road.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{accommodation > 0 ? accommodation.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{allowance > 0 ? allowance.toLocaleString() : ''}</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>{meal > 0 ? meal.toLocaleString() : ''}</td>
            <td style={{ ...cell, padding: 0 }}>
              <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '28%', textAlign: 'left', verticalAlign: 'middle', fontWeight: 'bold', borderRight: '1px dotted #555', padding: '3px 4px 3px 8px' }}>小計</td>
                    <td style={{ padding: '3px 6px' }}></td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Summary row */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0' }}>
        <tbody>
          <tr style={{ height: '40px' }}>
            <td style={{ ...header, width: '6%' }}>工番</td>
            <td style={{ ...cell, width: '14%' }}></td>
            <td style={{ ...header, width: '8%' }}>行先・期間<br /><span style={{ fontSize: '7px' }}>（14字）</span></td>
            <td style={{ ...cell, width: '26%', fontSize: '11px' }}>{data.purpose || ''}</td>
            <td style={{ ...header, width: '7%' }}>精算額</td>
            <td style={{ ...cell, width: '12%', textAlign: 'right', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#ddd' }}>
              ¥{total.toLocaleString()}
            </td>
            <td className="vertical-text" style={{ ...header, width: '3%' }}>経理部署</td>
            <td style={{ ...cell, width: '8%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px', fontWeight: 'bold' }}>
              承認
            </td>
            <td style={{ ...cell, width: '8%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px', fontWeight: 'bold' }}>
              照査
            </td>
            <td style={{ ...cell, width: '8%', verticalAlign: 'top', textAlign: 'center', padding: '4px 4px 0 4px', fontWeight: 'bold' }}>
              担当
            </td>
          </tr>
        </tbody>
      </table>

      {/* Notes */}
      <div style={{ marginTop: '5px', fontSize: '9px', lineHeight: '1.35' }}>
        <div><strong>注：</strong>領収書は、必ず添付してください。（宿泊、タクシー、有料道路、航空券等）</div>
        <div><strong>意：</strong>出発地、目的地の発着時間を必ず明記のこと</div>
        <div><strong>事：</strong>帰社後、1週間以内に精算のこと。</div>
        <div><strong>項：</strong>精算が何枚にもわたる時、1枚目の精算額欄及び仮払戻入額欄に合計金額を記入のこと。</div>
        <div style={{ marginTop: '4px' }}>申請者→所属長→経理部署（保管）</div>
      </div>
    </div>
      );
    })}
    </>
  );
}
